'use client';
import React from 'react';
import { LANGUAGES, LANGUAGE_COUNT, type LanguageCode } from '@/data/wordLists';
import { useTestConfig } from '@/context/TestConfigContext';
import { smoothScrollToTop } from '@/utils/scroll';

export default function LanguageGrid() {
  const { language, setLanguage } = useTestConfig();

  const pick = (code: LanguageCode) => {
    setLanguage(code);
    smoothScrollToTop();
  };

  return (
    <section className="max-w-screen-xl mx-auto px-6 lg:px-8 py-16">
      <div className="section-head flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <div>
          <span className="eyebrow">Languages</span>
          <h2 className="text-2xl md:text-3xl text-foreground mt-2">Type in your language</h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
          {LANGUAGE_COUNT} frequency-based word lists, each with its own personal best.
        </p>
      </div>

      {/* Collapsing hairline grid — cells share rules like a printed table */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        style={{ borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)' }}
      >
        {LANGUAGES.map((lang) => {
          const isActive = lang.code === language;
          return (
            <button
              key={`lang-grid-${lang.code}`}
              type="button"
              onClick={() => pick(lang.code)}
              aria-pressed={isActive}
              className="group relative flex flex-col items-start gap-3 px-5 py-6 text-left transition-colors"
              style={{
                borderRight: '1px solid var(--border)',
                borderBottom: '1px solid var(--border)',
                background: isActive ? 'var(--foreground)' : 'transparent',
                color: isActive ? 'var(--background)' : 'var(--foreground)',
              }}
            >
              <span className="text-2xl leading-none" role="img" aria-label={lang.label}>
                {lang.flag}
              </span>
              <span>
                <span className="block text-sm font-medium">{lang.label}</span>
                <span
                  className="block text-xs mt-1"
                  style={{ color: isActive ? 'rgba(250,249,247,0.6)' : 'var(--faint)' }}
                >
                  {lang.nativeLabel}
                </span>
              </span>
              {!isActive && (
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: 'var(--muted)', mixBlendMode: 'multiply' }}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}

        {/* Filler cells keep the block a clean rectangle on the last row */}
        {Array.from({ length: (5 - (LANGUAGES.length % 5)) % 5 }).map((_, i) => (
          <div
            key={`filler-${i}`}
            aria-hidden="true"
            className="hidden lg:block"
            style={{
              borderRight: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          />
        ))}
      </div>
    </section>
  );
}
